import React from "react";
import styled from "styled-components";
import { Column, InputText, Notice, Row, Select, Text } from "@ficatranquilo/contabilidade.com-ui";
import { formatCompetencia } from "@components/ow";
import {
  periodFromReference,
  formatPeriodLabel,
  currentReference,
} from "@helpers/competencePeriod";

/**
 * Campos do padrão de contrato, compartilhados pelas três telas que o editam:
 * cadastro, edição (as duas em ContractPatterns/ContractPatternForm) e o modal
 * aberto de dentro do formulário de contrato.
 *
 * O estado e a validação moram no useContractPatternForm; aqui só o JSX e as
 * regras de exibição condicional. Cada grupo ("Prazo da nota fiscal", "Regra de
 * pagamento", "Apuração de horas") é exportado sozinho, numa linha de até 3
 * campos e sem título: no formulário longo cada um vira uma FormBox, e o modal
 * (export default) empilha os grupos com um título em cima de cada.
 *
 * `errors` é um mapa id do campo → mensagem (os ids batem com os `field` do
 * validateAll do hook).
 */

// Entre os grupos, espaço para o erro do último campo (20px abaixo dele) não encostar no título seguinte
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
`;

// Largura total: dentro da FormBox da lib (inline-flex com quebra), um bloco sem largura
// encolhe até o conteúdo e fica ao lado do vizinho.
const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const DEADLINE_TYPES = [
  { value: "FIXED_DAY", label: "Dia fixo do mês" },
  { value: "BUSINESS_DAY", label: "N-ésimo dia útil" },
];

const RULE_TYPES = [
  ...DEADLINE_TYPES,
  { value: "LAST_DAY", label: "Último dia do mês" },
];

const MONTHS = [
  { value: "CURRENT", label: "Mês vigente" },
  { value: "NEXT", label: "Mês seguinte" },
];

const CLOSING_DAYS = Array.from({ length: 31 }, (_, index) => ({
  value: String(index + 1),
  label: `Dia ${index + 1}`,
}));

/** Regra da apuração de horas, em cima do dia de fechamento. */
const CLOSING_DAY_RULE = "Só vale para contratos por hora. Nos demais tipos o campo é ignorado.";

/** Opção do Select da lib para o valor em texto do hook (vazio → nenhuma). */
const pick = (options, value) => options.find((option) => option.value === value) || null;

function dayLabel(type) {
  return type === "FIXED_DAY" ? "Dia do mês (1 a 31)" : "N-ésimo dia útil (1 a 10)";
}

export function PatternNameField({ values, onChange, errors = {} }) {
  return (
    <Row>
      <Column $columns={8}>
        <InputText
          $idForm="pattern-name"
          $label="Nome do padrão"
          $variant="default"
          $isRequired
          $errors={errors["pattern-name"]}
          value={values.name}
          onChange={(e) => onChange("name", e.target.value)}
          placeholder="Ex.: Desenvolvedores, Consultores"
        />
      </Column>
    </Row>
  );
}

export function InvoiceDeadlineFields({ values, onChange, errors = {} }) {
  const { invoiceDeadlineType, invoiceDeadline, invoiceDeadlineMonth } = values;
  return (
    <Row $gap={20}>
      <Column $columns={4}>
        <Select
          $idForm="pattern-invoice-type"
          $label="Tipo de prazo"
          $isRequired
          $options={DEADLINE_TYPES}
          $errors={errors["pattern-invoice-type"]}
          value={pick(DEADLINE_TYPES, invoiceDeadlineType)}
          onChange={(option) => {
            onChange("invoiceDeadlineType", option ? option.value : "");
            onChange("invoiceDeadline", "");
          }}
        />
      </Column>
      {invoiceDeadlineType && (
        <Column $columns={4}>
          <InputText
            $idForm="pattern-invoice-day"
            $label={dayLabel(invoiceDeadlineType)}
            $variant="default"
            $isRequired
            $errors={errors["pattern-invoice-day"]}
            type="number"
            min="1"
            max={invoiceDeadlineType === "FIXED_DAY" ? 31 : 10}
            value={invoiceDeadline}
            onChange={(e) => onChange("invoiceDeadline", e.target.value)}
            placeholder={invoiceDeadlineType === "FIXED_DAY" ? "Ex.: 10" : "Ex.: 5"}
          />
        </Column>
      )}
      {invoiceDeadlineType && (
        <Column $columns={4}>
          <Select
            $idForm="pattern-invoice-month"
            $label="Mês de referência"
            $isRequired
            $hasTooltip="Se o prazo é no próprio mês trabalhado ou no mês seguinte."
            $options={MONTHS}
            value={pick(MONTHS, invoiceDeadlineMonth)}
            onChange={(option) => option && onChange("invoiceDeadlineMonth", option.value)}
          />
        </Column>
      )}
    </Row>
  );
}

export function PaymentRuleFields({ values, onChange, errors = {} }) {
  const { paymentRuleType, paymentRuleValue, paymentRuleMonth } = values;
  return (
    <Row $gap={20}>
      <Column $columns={4}>
        <Select
          $idForm="pattern-payment-type"
          $label="Tipo de regra"
          $isRequired
          $options={RULE_TYPES}
          $errors={errors["pattern-payment-type"]}
          value={pick(RULE_TYPES, paymentRuleType)}
          onChange={(option) => {
            onChange("paymentRuleType", option ? option.value : "");
            onChange("paymentRuleValue", "");
          }}
        />
      </Column>
      {(paymentRuleType === "FIXED_DAY" || paymentRuleType === "BUSINESS_DAY") && (
        <Column $columns={4}>
          <InputText
            $idForm="pattern-payment-day"
            $label={dayLabel(paymentRuleType)}
            $variant="default"
            $isRequired
            $errors={errors["pattern-payment-day"]}
            type="number"
            min="1"
            max={paymentRuleType === "FIXED_DAY" ? 31 : 10}
            value={paymentRuleValue}
            onChange={(e) => onChange("paymentRuleValue", e.target.value)}
            placeholder={paymentRuleType === "FIXED_DAY" ? "Ex.: 20" : "Ex.: 5"}
          />
        </Column>
      )}
      {paymentRuleType && (
        <Column $columns={4}>
          <Select
            $idForm="pattern-payment-month"
            $label="Mês de referência"
            $isRequired
            $hasTooltip="Se o pagamento ocorre no próprio mês trabalhado ou no mês seguinte."
            $options={MONTHS}
            value={pick(MONTHS, paymentRuleMonth)}
            onChange={(option) => option && onChange("paymentRuleMonth", option.value)}
          />
        </Column>
      )}
    </Row>
  );
}

/** Dia de fechamento, com a regra da apuração em cima (a mesma no formulário e no modal). */
export function ClosingDayFields({ values, onChange, errors = {} }) {
  const { closingDay } = values;

  // Prévia do que o dia de fechamento produziria na competência atual
  const reference = currentReference();
  const [refYear, refMonth] = reference.split("-").map(Number);
  const previewPeriod = periodFromReference(
    reference,
    closingDay === "" ? null : parseInt(closingDay, 10)
  );

  return (
    <Group>
      <Text $text={CLOSING_DAY_RULE} $variant="caption_medium" $color="N400" />
      <Row>
        <Column $columns={6}>
          <Select
            $idForm="pattern-closing-day"
            $label="Dia de fechamento"
            $hasTooltip="Dia em que as horas da competência param de ser apuradas."
            $isClearable
            $options={CLOSING_DAYS}
            $errors={errors["pattern-closing-day"]}
            placeholder="Último dia do mês (padrão)"
            value={pick(CLOSING_DAYS, closingDay)}
            onChange={(option) => onChange("closingDay", option ? option.value : "")}
          />
        </Column>
      </Row>
      {closingDay && (
        <Notice
          $variant="gray"
          $title={`A competência ${formatCompetencia({ month: refMonth, year: refYear })} apura de ${formatPeriodLabel(previewPeriod.start, previewPeriod.end)}`}
          $text="Nenhum dia é descartado: o que vier depois do fechamento é acertado na competência seguinte. Quando o dia não existe no mês (30 em fevereiro), a apuração encerra no último dia dele. A competência continua sendo o mês inteiro; o fechamento só decide até quando ela é medida."
        />
      )}
    </Group>
  );
}

/** Os grupos empilhados, com título em cima de cada um (o layout do modal). */
export default function ContractPatternFields({ values, onChange, errors }) {
  const props = { values, onChange, errors };
  return (
    <Stack>
      <PatternNameField {...props} />

      <Group>
        <Text $text="Prazo da nota fiscal" $variant="paragraph_big_medium" $color="N900" />
        <InvoiceDeadlineFields {...props} />
      </Group>

      <Group>
        <Text $text="Regra de pagamento" $variant="paragraph_big_medium" $color="N900" />
        <PaymentRuleFields {...props} />
      </Group>

      <Group>
        <Text $text="Apuração de horas" $variant="paragraph_big_medium" $color="N900" />
        <ClosingDayFields {...props} />
      </Group>
    </Stack>
  );
}
